// /src/components/GmailSignatureGenerator.js
import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { uploadImageToStorage } from '../firebase';

const Container = styled.div`
  display: flex;
  height: calc(100vh - 60px);
  background: #f8fafc;
  overflow: hidden;
`;

const LeftNav = styled.div`
  width: 80px;
  background: white;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 60px;
  bottom: 0;
  z-index: 100;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
`;

const NavItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.active ? '#3b82f6' : '#64748b'};
  background: ${props => props.active ? '#eff6ff' : 'transparent'};
  font-weight: 500;
  font-size: 11px;
  border-right: 2px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  
  &:hover {
    background: ${props => props.active ? '#eff6ff' : '#f8fafc'};
    color: ${props => props.active ? '#3b82f6' : '#1e293b'};
  }
`;

const NavIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  
  svg {
    width: 100%;
    height: 100%;
    fill: currentColor;
  }
`;

const MainFormArea = styled.div`
  margin-left: 80px;
  width: calc(40% - 80px);
  background: white;
  border-right: 1px solid #e2e8f0;
  position: fixed;
  left: 0;
  top: 60px;
  bottom: 0;
  overflow-y: auto;
`;

const FormContent = styled.div`
  padding: 0 32px 32px 32px;
  height: 100%;
`;

const StepHeader = styled.div`
  margin-bottom: 28px;
  padding: 24px 0 18px 0;
  border-bottom: 1px solid #e5e7eb;
  background: white;
`;

const StepNumber = styled.div`
  font-size: 12px;
  color: #3b82f6;
  margin-bottom: 6px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StepTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.3;
`;

const RequiredNote = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 8px 0 24px 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 18px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #fafafa;
  color: #1f2937;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:hover {
    border-color: #9ca3af;
    background: white;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #fafafa;
  color: #1f2937;
  min-height: 75px;
  resize: vertical;
  transition: all 0.2s ease;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:hover {
    border-color: #9ca3af;
    background: white;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const ValidationIcon = styled.div`
  color: #10b981;
  font-size: 14px;
  margin-left: 8px;
  font-weight: 600;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
`;

const SectionDivider = styled.div`
  border-top: 1px solid #e5e7eb;
  margin: 24px 0 20px 0;
  padding-top: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px 0;
`;



const ToggleSwitch = styled.div`
  width: 44px;
  height: 24px;
  background: ${props => props.active ? '#3b82f6' : '#d1d5db'};
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: ${props => props.active ? '#2563eb' : '#9ca3af'};
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.active ? '22px' : '2px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: left 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;





const RightPreview = styled.div`
  width: 60%;
  margin-left: 40%;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  padding: 48px 40px 32px 40px;
  position: fixed;
  right: 0;
  top: 30px;
  bottom: 0;
  overflow-y: auto;
`;



const PreviewTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.3;
`;

const PreviewContent = styled.div`
  flex: 1;
  padding: 0;
  overflow-y: auto;
`;

const SigCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 18px;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
  }
`;

const SigHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const Badge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .03em;
  padding: 5px 10px;
  border-radius: 4px;
  text-transform: uppercase;
  background: ${props => props.variant === 'reply' ? '#eff6ff' : '#ecfdf5'};
  color: ${props => props.variant === 'reply' ? '#1e40af' : '#065f46'};
  border: 1px solid ${props => props.variant === 'reply' ? '#bfdbfe' : '#a7f3d0'};
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid #e5e7eb;
  margin: 28px 0;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 16px;
  padding-top: 2px;
`;

const HtmlCopyButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #2563eb;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const ImageUploadSection = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 6px;
  padding: 18px;
  text-align: center;
  margin-bottom: 18px;
  background: #f9fafb;
  transition: border-color 0.2s ease;
  
  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const ImagePreview = styled.div`
  margin-top: 15px;
  text-align: center;
`;

const ProfileImagePreview = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #10b981;
`;

const LogoPreview = styled.img`
  height: 24px;
  object-fit: contain;
`;

const SignaturePreview = styled.div``;

// Preview zoom wrappers for on-screen UI only (not in copied HTML)
const MainPreviewZoom = styled.div`
  transform: scale(1.2);
  transform-origin: top left;
  display: inline-block;
  -webkit-font-smoothing: antialiased;
`;

const CompactPreviewZoom = styled.div`
  transform: scale(1.15);
  transform-origin: top left;
  display: inline-block;
  -webkit-font-smoothing: antialiased;
`;


const GuideBox = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
`;

const DisclaimerBox = styled.div`
  background: #fffbeb;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 13px;
  line-height: 1.4;
  color: #92400e;
`;

const DisclaimerTitle = styled.div`
  font-weight: 600;
  margin-bottom: 6px;
  color: #b45309;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
`;

const DisclaimerIcon = styled.div`
  width: 14px;
  height: 14px;
  background: #f59e0b;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  font-weight: bold;
  flex-shrink: 0;
`;

const AutoFillButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  border: 1px solid #3b82f6;
  background: #3b82f6;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: #2563eb;
    border-color: #2563eb;
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const PreviewHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0 12px 0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 18px;
`;

const UploadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #3b82f6;
  font-size: 14px;
  margin-top: 8px;
  
  &::before {
    content: '';
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const UploadHint = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.4;
`;

const DimensionHint = styled.div`
  font-size: 12px;
  color: #059669;
  background: #ecfdf5;
  border: 1px solid #d1fae5;
  border-radius: 4px;
  padding: 8px 10px;
  margin-top: 8px;
  line-height: 1.4;
`;

// Fallback: copy HTML by writing it into a hidden, contenteditable element and using execCommand('copy')
function copyHtmlUsingHiddenArea(html) {
  const hidden = document.createElement('div');
  hidden.setAttribute('contenteditable', 'true');
  hidden.style.position = 'fixed';
  hidden.style.left = '-9999px';
  hidden.style.top = '0';
  hidden.style.opacity = '0';
  hidden.style.pointerEvents = 'none';
  // Use a wrapper to ensure full table selection in Gmail
  hidden.innerHTML = `<div>${html}</div>`;
  document.body.appendChild(hidden);
  const range = document.createRange();
  range.selectNodeContents(hidden);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch (e) {
    ok = false;
  }
  sel.removeAllRanges();
  document.body.removeChild(hidden);
  return ok;
}

// --- Clipboard helpers: copy as real HTML so Gmail pastes rich content ---
async function copyRichHtml(html, onSuccess, onError) {
  try {
    // Prefer async Clipboard API when available & permitted
    if (navigator.clipboard && window.ClipboardItem) {
      const item = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([html.replace(/<[^>]+>/g, '')], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      onSuccess && onSuccess('Signature copied to clipboard!');
      return;
    }

    // Try writeText with text/html wrapper (some Chromium builds will paste rich if focus target supports it)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(html);
      onSuccess && onSuccess('Signature copied to clipboard!');
      return;
    }

    // Fallback: hidden contenteditable + execCommand
    const ok = copyHtmlUsingHiddenArea(html);
    if (ok) {
      onSuccess && onSuccess('Signature copied to clipboard!');
      return;
    }

    onError && onError('Copy not allowed by the browser.');
  } catch (err) {
    // Final fallback: hidden contenteditable; do NOT open a new window
    const ok = copyHtmlUsingHiddenArea(html);
    if (ok) {
      onSuccess && onSuccess('Signature copied to clipboard!');
    } else {
      onError && onError('Copy failed. Check browser permissions and try again.');
    }
  }
}

const GmailSignatureGenerator = ({ user }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    name: 'Kanav Hasija',
    title: 'Cofounder & CEO',
    phone: '+1 (551) 242-4281',
    address: '2120 University Ave, Berkeley, CA 94704, United States',
    websiteUrl: 'https://meltplan.com',
    linkedinUrl: 'https://linkedin.com/in/kanavhasija',
    eventText: 'Your Pre-Construction Productivity Platform',
    ctaUrl: 'https://meltplan.com/',
    showEventBanner: true
  });

  const [images, setImages] = useState({
    profileImage: null,
    profileImageUrl: 'https://cdn.prod.website-files.com/6834f4d32a2949a1de9d8f0f/689cbd3e4bad857be713695b_kanav.png',
    logoImage: null,
    logoImageUrl: 'https://cdn.prod.website-files.com/6834f4d32a2949a1de9d8f0f/689dfb5a305eb43a36792cf5_8c4c6df83b79290ba7016c9011587b48_logo.png'
  });

  const [uploadingImages, setUploadingImages] = useState({
    profileImage: false,
    logoImage: false
  });


  const signatureRef = useRef(null);

  // Function to auto-fill from Google profile
  const autoFillFromGoogle = () => {
    if (user) {
      if (user.displayName) {
        setFormData(prev => ({
          ...prev,
          name: user.displayName
        }));
      }
      
      if (user.photoURL) {
        setImages(prev => ({
          ...prev,
          profileImageUrl: user.photoURL
        }));
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Please select an image smaller than 5MB.');
      return;
    }

    try {
      // Set uploading state
      setUploadingImages(prev => ({ ...prev, [type]: true }));

      // Upload to Firebase Storage
      const downloadURL = await uploadImageToStorage(file, 'signature-images');

      // Update images state with Firebase URL
      setImages(prev => ({ 
        ...prev, 
        [type]: file, 
        [`${type}Url`]: downloadURL 
      }));

    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      // Clear uploading state
      setUploadingImages(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleImageUrlChange = (e, type) => {
    const url = e.target.value;
    setImages(prev => ({ ...prev, [`${type}Url`]: url }));
  };

  const generateTableBasedSignature = () => {
    const profileImg = images.profileImageUrl || 'https://cdn.prod.website-files.com/6834f4d32a2949a1de9d8f0f/689cbd3e4bad857be713695b_kanav.png';
    const logoImg = images.logoImageUrl || 'https://cdn.prod.website-files.com/6834f4d32a2949a1de9d8f0f/689dfb5a305eb43a36792cf5_8c4c6df83b79290ba7016c9011587b48_logo.png';
    const phoneHref = (formData.phone || '').replace(/\s/g, '');
    const websiteUrl = formData.websiteUrl || 'https://meltplan.com/';
    const linkedinUrl = formData.linkedinUrl || 'https://linkedin.com/';
    const bannerText = formData.eventText || 'Your Pre-Construction Productivity Platform';
    const ctaUrl = formData.ctaUrl || 'https://meltplan.com/';
  
    const bannerBlock = formData.showEventBanner ? `
  <tr><td colspan="3" style="padding:0;height:8px;line-height:8px;font-size:0">&nbsp;</td></tr>
  <tr>
    <td colspan="3" style="padding:8px 0 0">
      <table cellpadding="0" cellspacing="0" width="100%" style="margin:0;padding:0;background:#F7F6FB;border-radius:10px">
        <tbody>
          <tr>
            <td style="padding:8px 10px;font-size:10px;color:#523723">${bannerText}</td>
            <td align="right" style="padding:6px 10px">
              <table cellpadding="0" cellspacing="0" style="margin:0;padding:0">
                <tbody><tr>
                  <td align="center" style="padding:0;background:#0e8c6a;border-radius:14px">
                    <a href="${ctaUrl}" style="margin:0;padding:6px 10px;display:inline-block;color:#fff;font-weight:700;font-size:10px" target="_blank">Book a demo</a>
                  </td>
                </tr></tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  </tr>` : '';
  
    return `
  <table cellpadding="0" cellspacing="0" border="0" style="margin:0;padding:0;font-size:medium;border-collapse:collapse;color:#1a1a1a;max-width:400px;width:370px;background:transparent!important">
    <tbody>
      <tr>
        <td width="199" style="padding:0 12px 0 0;width:199px;vertical-align:top">
          <table cellpadding="0" cellspacing="0" width="100%" style="margin:0;padding:0;border-collapse:collapse;background:transparent!important">
            <tbody>
              <tr>
                <td valign="top" style="padding:0 8px 0 0;width:45px">
                  <img src="${profileImg}" width="45" height="45" alt="" style="margin:0;padding:0;display:block;border-radius:50%;border:0;outline:none">
                </td>
                <td valign="middle" style="padding:0">
                  <div style="margin:0;padding:0;font-size:12px;font-weight:700;line-height:1.2;word-break:break-word;max-width:100%">${formData.name}</div>
                  <div style="margin:0;padding:2px 0 0;font-size:10px;color:#6b6f76;line-height:1.2">${formData.title}</div>
                </td>
              </tr>
              <tr><td colspan="2" style="padding:0;height:4px;line-height:4px;font-size:0">&nbsp;</td></tr>
              <tr>
                <td colspan="2" style="padding:8px 0 0">
                  <img src="${logoImg}" width="90" alt="MeltPlan" style="margin:0;padding:0;display:block;border:0;outline:none">
                </td>
              </tr>
            </tbody>
          </table>
        </td>
        <td style="padding:0;width:1px;background:#e5e7eb"></td>
        <td width="170" style="padding:0 0 0 10px;width:170px;vertical-align:top">
          <table cellpadding="0" cellspacing="0" width="170" style="margin:0;padding:0;border-collapse:collapse;background:transparent!important;width:170px">
            <tbody>
              <tr>
                <td style="padding:0;height:18px;vertical-align:middle">
                  <table cellpadding="0" cellspacing="0" style="margin:0;padding:0;border-collapse:collapse">
                    <tbody><tr>
                      <td width="14" style="padding:0 6px 0 0;vertical-align:middle">
                        <img src="https://48938617.fs1.hubspotusercontent-na2.net/hubfs/48938617/signature-icons/call.png" width="13" alt="" style="margin:0;padding:0;display:block">
                      </td>
                      <td style="padding:0;font-size:10px;line-height:1.2">
                        <a href="tel:${phoneHref}" style="margin:0;padding:0;color:#1F150D" target="_blank">${formData.phone}</a>
                      </td>
                    </tr></tbody>
                  </table>
                </td>
              </tr>
              <tr>
                <td style="padding:8px 0 0;height:18px;vertical-align:middle">
                  <table cellpadding="0" cellspacing="0" style="margin:0;padding:0;border-collapse:collapse">
                    <tbody><tr>
                      <td width="12" style="padding:0 6px 0 0;vertical-align:middle">
                        <img src="https://48938617.fs1.hubspotusercontent-na2.net/hubfs/48938617/signature-icons/location_on.png" width="13" alt="" style="margin:0;padding:0;display:block">
                      </td>
                      <td style="padding:0;font-size:10px;line-height:1.2">
                        <a style="margin:0;padding:0;color:#1F150D" target="_blank">${formData.address}</a>
                      </td>
                    </tr></tbody>
                  </table>
                </td>
              </tr>
              <tr>
                <td style="padding:8px 0 0;height:18px;vertical-align:middle">
                  <table cellpadding="0" cellspacing="0" style="margin:0;padding:0;border-collapse:collapse">
                    <tbody><tr>
                      <td width="12" style="padding:0 6px 0 0;vertical-align:middle">
                        <img src="https://cdn.prod.website-files.com/6834f4d32a2949a1de9d8f0f/689cbd38c7c93270f400550a_language.png" width="13" alt="" style="margin:0;padding:0;display:block">
                      </td>
                      <td style="padding:0 0px 0 0;font-size:10px;line-height:1.2">
                        <a href="${websiteUrl}" style="margin:0;padding:0;color:#0D6352;font-weight:600" target="_blank">MeltPlan</a>
                      </td>
                      <td style="padding:0 6px;color:#c7c9cc">/</td>
                      <td width="12" style="padding:0 6px 0 0;vertical-align:middle">
                        <img src="https://48938617.fs1.hubspotusercontent-na2.net/hubfs/48938617/signature-icons/linkedin_icon.png" width="13" alt="" style="margin:0;padding:0;display:block">
                      </td>
                      <td style="padding:0 0px;font-size:10px;line-height:1.2">
                        <a href="${linkedinUrl}" style="margin:0;padding:0;color:#0D6352;font-weight:600" target="_blank">LinkedIn</a>
                      </td>
                    </tr></tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
      ${bannerBlock}
    </tbody>
  </table>`;
  };

const generateTableBasedSignatureV2 = () => {
    const logoImg = images.logoImageUrl || 'https://cdn.prod.website-files.com/6834f4d32a2949a1de9d8f0f/689dfb5a305eb43a36792cf5_8c4c6df83b79290ba7016c9011587b48_logo.png';
  const websiteUrl = formData.websiteUrl || 'https://meltplan.com';

  return `
<table cellpadding="0" cellspacing="0" border="0"
       style="margin:0;padding:0;font-size:medium;border-collapse:collapse;color:#1a1a1a;max-width:400px;width:370px;background:transparent!important">
  <tbody>
    <tr>
      <td valign="top" style="padding:0">
        <table cellpadding="0" cellspacing="0" style="margin:0;padding:0;border-collapse:collapse;background:transparent!important">
          <tbody>
            <tr>
              <td style="padding:0">
                <div style="margin:0;padding:0;font-size:12px;font-weight:700;line-height:1.2">${formData.name}</div>
                <div style="margin:0;padding:2px 0 0;font-size:10px;color:#6b6f76;line-height:1.2">${formData.title}</div>
              </td>
            </tr>
            <tr><td style="padding:0;height:4px;line-height:4px;font-size:0">&nbsp;</td></tr>
            <tr>
              <td style="padding:8px 0 0">
                <img src="${logoImg}" width="90" alt="MeltPlan" style="margin:0;padding:0;display:block;border:0;outline:none">
              </td>
            </tr>
            <tr>
              <td style="padding:10px 0 0">
                <a href="${websiteUrl}" target="_blank"
                   style="margin:0;padding:0;color:#0D6352;font-weight:600;font-size:10px;text-decoration:underline;">
                   www.meltplan.com
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </td>
    </tr>
  </tbody>
</table>`;
};

  const showToast = (msg) => {
    console.log(msg);
  };

  const copyHtmlSignature = async () => {
    const html = generateTableBasedSignature();
    await copyRichHtml(html, showToast, showToast);
  };

  const copyHtmlSignatureV2 = async () => {
    const html = generateTableBasedSignatureV2();
    await copyRichHtml(html, showToast, showToast);
  };

  const renderDetailsTab = () => (
    <>
      <FormGrid>
        <FormGroup>
          <Label>Full Name*</Label>
          <InputRow>
            <Input 
              name="name" 
              value={formData.name} 
              onChange={handleInputChange} 
              placeholder="Enter first name"
            />
            {formData.name && <ValidationIcon>✓</ValidationIcon>}
          </InputRow>
        </FormGroup>

        <FormGroup>
          <Label>Job Title</Label>
          <InputRow>
            <Input 
              name="title" 
              value={formData.title} 
              onChange={handleInputChange} 
              placeholder="Enter job title"
            />
            {formData.title && <ValidationIcon>✓</ValidationIcon>}
          </InputRow>
        </FormGroup>
      </FormGrid>

      <FormGrid>
        <FormGroup>
          <Label>Phone Number</Label>
          <InputRow>
            <Input 
              name="phone" 
              value={formData.phone} 
              onChange={handleInputChange} 
              placeholder="Enter phone number"
            />
            {formData.phone && <ValidationIcon>✓</ValidationIcon>}
          </InputRow>
        </FormGroup>

        <FormGroup>
          <Label>Website URL</Label>
          <InputRow>
            <Input 
              name="websiteUrl" 
              value={formData.websiteUrl} 
              onChange={handleInputChange} 
              placeholder="Enter website URL"
            />
            {formData.websiteUrl && <ValidationIcon>✓</ValidationIcon>}
          </InputRow>
        </FormGroup>
      </FormGrid>

      <FormGroup>
        <Label>Address</Label>
        <TextArea 
          name="address" 
          value={formData.address} 
          onChange={handleInputChange} 
          placeholder="Enter full address"
        />
      </FormGroup>

      <FormGroup>
        <Label>LinkedIn URL</Label>
        <Input 
          name="linkedinUrl" 
          value={formData.linkedinUrl} 
          onChange={handleInputChange} 
          placeholder="Enter LinkedIn URL"
        />
      </FormGroup>

      <SectionDivider>
        <SectionTitle>Email Signature Footer</SectionTitle>
      </SectionDivider>

      <FormGroup>
        <Label>Event Text</Label>
        <Input 
          name="eventText" 
          value={formData.eventText} 
          onChange={handleInputChange} 
          placeholder="Enter promotional message"
        />
      </FormGroup>

      <FormGroup>
        <Label>CTA URL</Label>
        <Input 
          name="ctaUrl" 
          value={formData.ctaUrl} 
          onChange={handleInputChange} 
          placeholder="Enter CTA URL"
        />
      </FormGroup>

      <ToggleRow style={{ marginBottom: '48px' }}>
        <Label>Show Event Banner in Signature</Label>
        <ToggleSwitch 
          active={formData.showEventBanner} 
          onClick={() => setFormData(prev => ({ ...prev, showEventBanner: !prev.showEventBanner }))}
        />
      </ToggleRow>
    </>
  );

  const renderImagesTab = () => (
    <>
      <div style={{ marginBottom: '24px', color: '#64748b', lineHeight: '1.6' }}>
        Add your profile photo or logo to enhance your email signature! You can upload files or use URLs from your hosted website.
      </div>

        <FormGroup>
          <Label>Profile Picture</Label>
          <ImageUploadSection>
            <div style={{ marginBottom: 15 }}><strong>Upload Image or Use URL</strong></div>
            <Input 
              type="file" 
              accept="image/*" 
              onChange={(e)=>handleImageUpload(e,'profileImage')} 
              style={{ marginBottom: 10 }} 
              disabled={uploadingImages.profileImage}
            />
            <UploadHint>Only images, max 5MB</UploadHint>
            <DimensionHint>
              <strong>💡 Profile Image Tips:</strong> Square images work best (e.g., 200×200px). The image will appear as a 50×50px circle in your signature.
            </DimensionHint>
            {uploadingImages.profileImage && <UploadingIndicator>Uploading image...</UploadingIndicator>}
            <div style={{ marginBottom: 10, marginTop: 10 }}>OR</div>
            <Input type="url" placeholder="Enter image URL" value={images.profileImageUrl}
                   onChange={(e)=>handleImageUrlChange(e,'profileImage')} />
            {images.profileImageUrl && <ImagePreview><ProfileImagePreview src={images.profileImageUrl} alt="Profile" /></ImagePreview>}
          </ImageUploadSection>
        </FormGroup>

        <FormGroup style={{ marginBottom: '48px' }}>
          <Label>Company Logo</Label>
          <ImageUploadSection>
            <div style={{ marginBottom: 15 }}><strong>Upload Logo or Use URL</strong></div>
            <Input 
              type="file" 
              accept="image/*" 
              onChange={(e)=>handleImageUpload(e,'logoImage')} 
              style={{ marginBottom: 10 }} 
              disabled={uploadingImages.logoImage}
            />
            <UploadHint>Only images, max 5MB</UploadHint>
            <DimensionHint>
              <strong>🏢 Logo Tips:</strong> Horizontal logos work best (e.g., 200×60px). The logo will appear at ~100px width in your signature.
            </DimensionHint>
            {uploadingImages.logoImage && <UploadingIndicator>Uploading logo...</UploadingIndicator>}
            <div style={{ marginBottom: 10, marginTop: 10 }}>OR</div>
            <Input type="url" placeholder="Enter logo URL" value={images.logoImageUrl}
                   onChange={(e)=>handleImageUrlChange(e,'logoImage')} />
            {images.logoImageUrl && <ImagePreview><LogoPreview src={images.logoImageUrl} alt="Logo" /></ImagePreview>}
          </ImageUploadSection>
        </FormGroup>
    </>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'details':
        return renderDetailsTab();
      case 'images':
        return renderImagesTab();
      default:
        return renderDetailsTab();
    }
  };

  return (
    <Container>
      <LeftNav>
        <NavItem 
          active={activeTab === 'details'} 
          onClick={() => setActiveTab('details')}
        >
          <NavIcon active={activeTab === 'details'}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </NavIcon>
          Details
        </NavItem>
        <NavItem 
          active={activeTab === 'images'} 
          onClick={() => setActiveTab('images')}
        >
          <NavIcon active={activeTab === 'images'}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>
          </NavIcon>
          Images
        </NavItem>
      </LeftNav>

      <MainFormArea>
        <FormContent>
          <StepHeader>
            <StepNumber>{activeTab === 'details' ? 'Step 1: Details' : 'Step 2: Images'}</StepNumber>
            <StepTitle>{activeTab === 'details' ? 'Enter Your Signature Details' : 'Upload Your Images'}</StepTitle>
            <RequiredNote>* Indicates a required field</RequiredNote>
          </StepHeader>
          {renderContent()}
        </FormContent>
      </MainFormArea>

      <RightPreview>
        <PreviewHeaderRow>
          <PreviewTitle style={{fontSize: 24}}>Signatures Preview</PreviewTitle>
          <AutoFillButton onClick={autoFillFromGoogle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
            Auto-fill from Google
          </AutoFillButton>
        </PreviewHeaderRow>

        <DisclaimerBox>
          <DisclaimerTitle>
            <DisclaimerIcon>!</DisclaimerIcon>
            Template Data Notice
          </DisclaimerTitle>
          The signature currently shows template information (Kanav's details).Update all fields with your information. <br />Use "Auto-fill from Google" to quickly populate <strong>name</strong> and <strong>profile image</strong>.
        </DisclaimerBox>

        <PreviewContent>
        {/* New Email signature */}
        <SigCard>
          <SigHeaderRow>
            <Badge>FOR NEW EMAILS USE</Badge>
          </SigHeaderRow>
            <SignaturePreview ref={signatureRef} style={{marginTop:20}}>
            <MainPreviewZoom>
              <div dangerouslySetInnerHTML={{ __html: generateTableBasedSignature() }} />
            </MainPreviewZoom>
          </SignaturePreview>
            <ButtonRow style={{marginTop: 50}}>
            <HtmlCopyButton onClick={copyHtmlSignature}>Copy Signature</HtmlCopyButton>
          </ButtonRow>
        </SigCard>

        <SigCard>
          <SigHeaderRow>
            <Badge variant="reply">ON REPLY/FORWARD USE</Badge>
          </SigHeaderRow>
            <div style={{ display: 'flex', justifyContent: 'left', marginTop: 20 }}>
            <CompactPreviewZoom>
              <div dangerouslySetInnerHTML={{ __html: generateTableBasedSignatureV2() }} />
            </CompactPreviewZoom>
          </div>
            <ButtonRow style={{marginTop: 50}}>
            <HtmlCopyButton onClick={copyHtmlSignatureV2}>Copy Signature</HtmlCopyButton>
          </ButtonRow>
        </SigCard>

          <Divider />
          <GuideBox>
            <h3 style={{ margin: '0 0 16px', color: '#1e293b', fontSize: '18px' }}>How to add these in Gmail</h3>
            <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7, color: '#374151' }}>
              <li style={{ marginBottom: 12 }}>
                <strong>Add the New‑Email signature:</strong> Click <em>Copy Signature</em> above → In Gmail go to
              <em> Settings → See all settings → General → Signature</em> → <strong>Create new</strong> →
              name it (e.g., <em>Meltplan</em>) → <strong>Paste</strong> the signature → click <strong>Save</strong>.
            </li>
              <li style={{ marginBottom: 12 }}>
                <strong>Add the Reply/Forward signature:</strong> Click <em>Copy Signature</em> above →
              back in the same Gmail Signatures area → <strong>Create new</strong> →
              name it (e.g., <em>Signature Kanav</em>) → <strong>Paste</strong> → click <strong>Save</strong>.
            </li>
              <li style={{ marginBottom: 12 }}>
              <strong>Set Signature defaults:</strong> In <em>Signature defaults</em>,
              set <strong>FOR NEW EMAILS USE</strong> to your first signature (e.g., <em>Meltplan</em>) and
              <strong> ON REPLY/FORWARD USE</strong> to your second signature (e.g., <em>Signature Kanav</em>).
            </li>
              <li style={{ marginBottom: 12 }}>
              <strong>Final tip:</strong> Make sure the Gmail compose zoom is <strong>100%</strong> and the editor
                text size is <strong>Normal</strong>. If links aren't clickable, re‑paste using the copy buttons above.
            </li>
          </ol>
        </GuideBox>
        </PreviewContent>
      </RightPreview>
    </Container>
  );
};

export default GmailSignatureGenerator;